import { useAuth } from '@clerk/clerk-react';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';

interface ServerToClientEvents {
  'message:new': (message: {
    id: string;
    content: string;
    senderId: string;
    senderName: string;
    senderType: string;
    teamId: string;
    channel: 'team' | 'help';
    createdAt: string;
    updatedAt: string;
  }) => void;
  error: (error: { message: string }) => void;
}

interface ClientToServerEvents {
  'facilitator:join': (
    data: { eventId: string },
    callback: (response: { success: boolean; error?: string }) => void,
  ) => void;
  'facilitator:leave': (
    data: { eventId: string },
    callback: (response: { success: boolean; error?: string }) => void,
  ) => void;
}

type TypedSocket = Socket<ServerToClientEvents, ClientToServerEvents>;

/**
 * Hook for facilitators to connect to Socket.IO and receive real-time message updates
 */
export function useFacilitatorSocket(eventId: string | undefined) {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  const socketRef = useRef<TypedSocket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Handle new message event
  const handleNewMessage = useCallback(
    (message: ServerToClientEvents['message:new'] extends (msg: infer T) => void ? T : never) => {
      // Only handle help channel messages
      if (message.channel !== 'help') return;

      // Invalidate queries to refetch data
      queryClient.invalidateQueries({ queryKey: ['TeamMessages', eventId, message.teamId, 'help'] });
      queryClient.invalidateQueries({ queryKey: ['TeamsNeedingResponse', eventId] });
    },
    [eventId, queryClient],
  );

  useEffect(() => {
    if (!eventId) return;

    let socket: TypedSocket | null = null;

    const connectSocket = async () => {
      try {
        // Get Clerk session token
        const token = await getToken();
        if (!token) {
          console.warn('No auth token available for socket connection');
          return;
        }

        // Determine socket URL from current location
        const socketUrl = import.meta.env.VITE_API_URL || window.location.origin;

        // Create socket connection
        socket = io(socketUrl, {
          path: '/api/socket',
          transports: ['websocket', 'polling'],
          auth: { token },
          reconnection: true,
          reconnectionDelay: 1000,
          reconnectionDelayMax: 5000,
          reconnectionAttempts: 10,
        }) as TypedSocket;

        socketRef.current = socket;

        socket.on('connect', () => {
          console.log('[FacilitatorSocket] Connected');
          setIsConnected(true);

          // Join facilitators room for this event
          socket?.emit('facilitator:join', { eventId }, (response) => {
            if (response.success) {
              console.log('[FacilitatorSocket] Joined facilitators room for event:', eventId);
            } else {
              console.error('[FacilitatorSocket] Failed to join room:', response.error);
            }
          });
        });

        socket.on('disconnect', (reason) => {
          console.log('[FacilitatorSocket] Disconnected:', reason);
          setIsConnected(false);
        });

        socket.on('error', (error) => {
          console.error('[FacilitatorSocket] Error:', error.message);
        });

        socket.on('message:new', handleNewMessage);

        socket.connect();
      } catch (error) {
        console.error('[FacilitatorSocket] Connection error:', error);
      }
    };

    connectSocket();

    // Cleanup on unmount or eventId change
    return () => {
      if (socket) {
        socket.emit('facilitator:leave', { eventId }, () => {});
        socket.disconnect();
        socketRef.current = null;
        setIsConnected(false);
      }
    };
  }, [eventId, getToken, handleNewMessage]);

  return { isConnected };
}
